/*
  Volume tier widget (snippets/volume-tiers.liquid).
  Lets each unit in a tier be a different colour product, and a different
  variant (size etc.) of that product, then sends all units to the main product
  form as items[]. DealEasy applies the discount in cart.
*/
if (!customElements.get('volume-tiers')) {
  customElements.define('volume-tiers', class VolumeTiers extends HTMLElement {
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-volume-tiers-config]').textContent);
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;

      this.storageKey = this.dataset.storageKey;
      this.tiers = Array.from(this.querySelectorAll('.volume-tiers__tier')).map((el) => ({
        el,
        quantity: parseInt(el.dataset.quantity, 10),
        percent: parseFloat(el.dataset.percent) || 0,
        openEnded: el.dataset.openEnded === 'true',
        radio: el.querySelector('.volume-tiers__radio'),
        unitsWrap: el.querySelector('[data-units]'),
        unitList: el.querySelector('[data-unit-list]'),
        count: el.querySelector('[data-count]'),
        total: el.querySelector('[data-total]'),
        compare: el.querySelector('[data-compare]'),
        saved: el.querySelector('[data-saved]')
      }));

      const template = this.querySelector('[data-swatch-template]');
      this.swatchRow = template ? template.content.firstElementChild : null;
      this.readProducts();
      this.defaultVariantId = this.findDefaultVariantId();

      const checked = this.tiers.findIndex((tier) => tier.radio.checked);
      this.tierIndex = checked > -1 ? checked : 0;
      // Variant ID for each unit. Kept longer than needed so a switch to a
      // smaller tier and back does not lose the customer's picks.
      this.units = [this.defaultVariantId];
      this.openCount = this.tiers[this.tiers.length - 1].quantity;
      this.restore();

      this.enableAddButton();
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      // Capture phase on document runs before product-form.js's own submit
      // handler on the form, so the items[] inputs are in place when it reads FormData.
      document.addEventListener('submit', this.onSubmitCapture.bind(this), true);

      this.render();
    }

    readProducts() {
      const products = JSON.parse(this.querySelector('[data-volume-tiers-products]').textContent);
      // The current product is always valid, even if it is not in the set collection.
      if (!products.some((product) => product.id === this.config.currentProduct.id)) {
        products.push(this.config.currentProduct);
      }

      this.products = {};
      this.variants = {};
      products.forEach((product) => {
        product.available = product.variants.some((variant) => variant.available);
        this.products[product.id] = product;
        product.variants.forEach((variant) => {
          variant.product = product;
          this.variants[variant.id] = variant;
        });
      });
      this.currentProduct = this.products[this.config.currentProduct.id];
      // Only show a single-unit picker when there is a variant to choose.
      this.hasVariants = products.some((product) => product.hasVariants);
    }

    // The variant selected on the page, or the first in-stock variant of this product.
    findDefaultVariantId() {
      const current = this.variants[this.config.currentVariantId];
      if (current && current.available) return current.id;
      const first = this.currentProduct.variants.find((variant) => variant.available);
      return first ? first.id : this.config.currentVariantId;
    }

    // Best in-stock variant of a product for the wanted options: an exact match
    // first, otherwise the one that keeps the most of the wanted options.
    matchVariant(product, wanted, lockedPosition = -1) {
      let best = null;
      let bestScore = -1;
      product.variants.forEach((variant) => {
        if (!variant.available) return;
        if (lockedPosition > -1 && variant.options[lockedPosition] !== wanted[lockedPosition]) return;
        const score = variant.options.filter((value, index) => value === wanted[index]).length;
        if (score > bestScore) {
          best = variant;
          bestScore = score;
        }
      });
      return best;
    }

    get tier() {
      return this.tiers[this.tierIndex];
    }

    unitCount(tier = this.tier) {
      return tier.openEnded ? Math.max(tier.quantity, this.openCount) : tier.quantity;
    }

    selectedUnits() {
      const count = this.unitCount();
      // Without variants, a single-unit tier has no picker, so it adds the variant of this page.
      if (count === 1 && !this.hasVariants) return [this.defaultVariantId];
      while (this.units.length < count) this.units.push(this.defaultVariantId);
      return this.units.slice(0, count);
    }

    restore() {
      // In the theme editor, always show the "Default selected tier" setting.
      if (window.Shopify && Shopify.designMode) return;

      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(this.storageKey));
      } catch (e) {
        saved = null;
      }
      if (!saved) return;

      if (Number.isInteger(saved.tier) && this.tiers[saved.tier]) this.tierIndex = saved.tier;
      if (Number.isInteger(saved.openCount)) this.openCount = Math.min(saved.openCount, this.config.maxUnits);
      if (Array.isArray(saved.units) && saved.units.length) {
        // Replace variants that have sold out since the selection was saved.
        this.units = saved.units
          .slice(0, this.config.maxUnits)
          .map((id) => (this.variants[id] && this.variants[id].available ? id : this.defaultVariantId));
      }
    }

    save() {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify({
          tier: this.tierIndex,
          openCount: this.openCount,
          units: this.units
        }));
      } catch (e) {
        // Storage blocked (private mode etc.). The widget still works for this page view.
      }
    }

    // The theme disables the button when the page variant is sold out. The
    // theme option selectors are hidden and the widget only offers in-stock
    // variants, so the button must stay usable.
    enableAddButton() {
      const button = this.form.querySelector('[type="submit"]');
      if (!button || !button.disabled) return;
      button.disabled = false;
      const text = button.querySelector('[data-add-to-cart-text]');
      if (text && this.config.addToCartText) text.textContent = this.config.addToCartText;
    }

    onChange(event) {
      if (!event.target.classList.contains('volume-tiers__radio')) return;
      this.tierIndex = parseInt(event.target.value, 10);
      this.update();
    }

    onClick(event) {
      const swatch = event.target.closest('.volume-tiers__swatch');
      if (swatch && !swatch.disabled) {
        const unitIndex = this.unitIndexOf(swatch);
        const product = this.products[swatch.dataset.productId];
        const current = this.variants[this.units[unitIndex]];
        // Keep the same size etc. when the new colour has it in stock.
        const next = this.matchVariant(product, current.options);
        if (next) this.units[unitIndex] = next.id;
        this.update();
        this.refocus(unitIndex, `.volume-tiers__swatch[data-product-id="${swatch.dataset.productId}"]`);
        return;
      }

      const pill = event.target.closest('.volume-tiers__pill');
      if (pill && !pill.disabled) {
        const unitIndex = this.unitIndexOf(pill);
        const position = parseInt(pill.dataset.position, 10);
        const current = this.variants[this.units[unitIndex]];
        const wanted = current.options.slice();
        wanted[position] = pill.dataset.value;
        // The clicked value is fixed. Other options change only if that combination is sold out.
        const next = this.matchVariant(current.product, wanted, position);
        if (next) this.units[unitIndex] = next.id;
        this.update();
        this.refocus(unitIndex, `.volume-tiers__pill[data-position="${position}"][data-value="${CSS.escape(pill.dataset.value)}"]`);
        return;
      }

      const step = event.target.closest('[data-step]');
      if (step) {
        const tier = this.tiers[this.tiers.length - 1];
        const next = this.unitCount(tier) + parseInt(step.dataset.step, 10);
        this.openCount = Math.min(Math.max(next, tier.quantity), this.config.maxUnits);
        this.update();
      }
    }

    unitIndexOf(element) {
      return parseInt(element.closest('[data-unit-index]').dataset.unitIndex, 10);
    }

    // Rows are rebuilt on render, so put focus back on the same control.
    refocus(unitIndex, selector) {
      const again = this.querySelector(`[data-unit-index="${unitIndex}"] ${selector}`);
      if (again) again.focus();
    }

    onSubmitCapture(event) {
      const form = event.target;
      if (form === this.form) {
        this.syncFormInputs();
        return;
      }

      // Sticky add-to-cart bars only know the current colour. Send the customer
      // to the widget instead so the tier and colours are chosen there.
      if (form.id && (form.id.indexOf('sticky-bar-form-') === 0 || form.id.indexOf('sticky-desktop-form-') === 0)) {
        event.preventDefault();
        event.stopPropagation();
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.classList.remove('is-highlighted');
        // Restart the highlight animation.
        void this.offsetWidth;
        this.classList.add('is-highlighted');
      }
    }

    syncFormInputs() {
      this.form.querySelectorAll('[data-volume-tiers-input]').forEach((input) => input.remove());

      // Disable the single-item inputs so only items[] is sent.
      const formId = this.form.id;
      const singles = Array.from(this.form.querySelectorAll('[name="id"], [name="quantity"]'))
        .concat(Array.from(document.querySelectorAll(`[name="quantity"][form="${formId}"]`)));
      singles.forEach((input) => { input.disabled = true; });

      // Same variant picked more than once becomes one line with a higher quantity.
      const totals = new Map();
      this.selectedUnits().forEach((id) => totals.set(id, (totals.get(id) || 0) + 1));

      let index = 0;
      totals.forEach((quantity, id) => {
        this.appendHidden(`items[${index}][id]`, id);
        this.appendHidden(`items[${index}][quantity]`, quantity);
        index += 1;
      });
    }

    appendHidden(name, value) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.setAttribute('data-volume-tiers-input', '');
      this.form.appendChild(input);
    }

    update() {
      this.save();
      this.render();
    }

    render() {
      const units = this.selectedUnits();
      const defaultPrice = this.variants[this.defaultVariantId].price;

      this.tiers.forEach((tier, index) => {
        const selected = index === this.tierIndex;
        tier.el.classList.toggle('is-selected', selected);
        tier.radio.checked = selected;
        if (tier.unitsWrap) tier.unitsWrap.hidden = !selected;

        // Other tiers show the price as if every unit were the default variant.
        const prices = selected
          ? units.map((id) => this.variants[id].price)
          : new Array(this.unitCount(tier)).fill(defaultPrice);
        this.renderPrices(tier, prices);
      });

      if (this.tier.unitList) this.renderUnits(units);
      if (this.tier.count) this.tier.count.textContent = units.length;
    }

    renderPrices(tier, prices) {
      const subtotal = prices.reduce((sum, price) => sum + price, 0);
      // DealEasy discounts each unit and rounds that down to the cent, so a
      // $23.90 unit at 15% saves $3.58, not $3.585. Match it to avoid a cart mismatch.
      const discount = prices.reduce((sum, price) => sum + Math.floor((price * tier.percent) / 100), 0);
      tier.total.textContent = this.formatMoney(subtotal - discount);
      if (tier.compare) tier.compare.textContent = this.formatMoney(subtotal);
      if (tier.saved) tier.saved.textContent = this.formatMoney(discount);
    }

    renderUnits(units) {
      const list = this.tier.unitList;
      list.innerHTML = '';

      units.forEach((id, index) => {
        const variant = this.variants[id];
        const product = variant.product;
        const row = document.createElement('div');
        row.className = 'volume-tiers__unit';
        row.dataset.unitIndex = index;

        const head = document.createElement('div');
        head.className = 'volume-tiers__unit-head';
        const label = document.createElement('span');
        label.className = 'volume-tiers__unit-label';
        label.textContent = `${this.config.unitLabel} ${index + 1}: `;
        const name = document.createElement('span');
        name.className = 'volume-tiers__unit-name';
        name.textContent = product.hasVariants
          ? [product.colour].concat(variant.options).join(' · ')
          : product.colour;
        head.append(label, name);
        row.appendChild(head);

        if (this.swatchRow) {
          const swatches = this.swatchRow.cloneNode(true);
          swatches.setAttribute('aria-label', `${this.config.unitLabel} ${index + 1}`);
          swatches.querySelectorAll('.volume-tiers__swatch').forEach((swatch) => {
            const isSelected = parseInt(swatch.dataset.productId, 10) === product.id;
            swatch.setAttribute('aria-checked', isSelected);
            swatch.classList.toggle('is-selected', isSelected);
          });
          row.appendChild(swatches);
        }

        if (product.hasVariants) {
          product.optionNames.forEach((optionName, position) => {
            row.appendChild(this.renderOptionPills(product, variant, optionName, position, index));
          });
        }

        list.appendChild(row);
      });
    }

    renderOptionPills(product, variant, optionName, position, unitIndex) {
      const group = document.createElement('div');
      group.className = 'volume-tiers__pills';
      group.setAttribute('role', 'radiogroup');
      group.setAttribute('aria-label', `${this.config.unitLabel} ${unitIndex + 1} ${optionName}`);

      const values = [];
      product.variants.forEach((item) => {
        if (values.indexOf(item.options[position]) === -1) values.push(item.options[position]);
      });

      values.forEach((value) => {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'volume-tiers__pill';
        pill.textContent = value;
        pill.dataset.position = position;
        pill.dataset.value = value;
        pill.setAttribute('role', 'radio');
        const isSelected = variant.options[position] === value;
        pill.setAttribute('aria-checked', isSelected);
        pill.classList.toggle('is-selected', isSelected);
        // Disabled only when no in-stock variant has this value. With more than
        // one option, a click may also change the other options to an in-stock combination.
        pill.disabled = !product.variants.some((item) => item.available && item.options[position] === value);
        group.appendChild(pill);
      });

      return group;
    }

    formatMoney(cents) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, this.config.moneyFormat);
      }
      return `$${(cents / 100).toFixed(2)}`;
    }
  });
}
